import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './SignalWidget.css';

const SIGNAL_COLORS = {
  'STRONG SELL': '#ef4444',
  'SELL': '#f97316',
  'NEUTRAL': '#6b7280',
  'BUY': '#eab308',
  'STRONG BUY': '#22c55e',
};

const SIGNAL_LABEL_KEYS = {
  'STRONG SELL': 'stats.strongSell',
  'SELL': 'stats.sell',
  'NEUTRAL': 'stats.neutral',
  'BUY': 'stats.buy',
  'STRONG BUY': 'stats.strongBuy',
};

const SUB_INDICATORS = [
  { key: 'momentum', labelKey: 'stats.momentum' },
  { key: 'whaleFlow', labelKey: 'stats.whaleFlow' },
  { key: 'funding', labelKey: 'stats.funding' },
  { key: 'liquidity', labelKey: 'stats.liquidity' },
  { key: 'oiPressure', labelKey: 'stats.oiPressure' },
];

function SignalWidget({ signal }) {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);
  const timeoutRef = useRef(null);

  const composite = signal?.composite ?? 50;
  const label = signal?.label || 'NEUTRAL';
  const color = SIGNAL_COLORS[label] || '#6b7280';
  const needleAngle = -90 + (composite / 100) * 180;

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setShowTooltip(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setShowTooltip(false), 150);
  };

  if (!signal) return null;

  return (
    <div
      className="signal-widget"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {/* Mini gauge */}
      <div className="signal-widget-gauge">
        <svg viewBox="0 0 44 26" className="signal-widget-svg">
          {/* Arc segments */}
          <path d="M 4 24 A 18 18 0 0 1 13 8" stroke="#ef4444" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.4" />
          <path d="M 13 8 A 18 18 0 0 1 22 5.5" stroke="#f97316" strokeWidth="2.5" fill="none" opacity="0.4" />
          <path d="M 18 6 A 18 18 0 0 1 26 6" stroke="#6b7280" strokeWidth="2.5" fill="none" opacity="0.4" />
          <path d="M 22 5.5 A 18 18 0 0 1 31 8" stroke="#eab308" strokeWidth="2.5" fill="none" opacity="0.4" />
          <path d="M 31 8 A 18 18 0 0 1 40 24" stroke="#22c55e" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.4" />
          {/* Needle */}
          <line
            x1="22" y1="24" x2="22" y2="8"
            stroke={color} strokeWidth="1.5" strokeLinecap="round"
            transform={`rotate(${needleAngle}, 22, 24)`}
          />
          <circle cx="22" cy="24" r="1.5" fill={color} />
        </svg>
      </div>
      <span className="signal-widget-score" style={{ color }}>{Math.round(composite)}</span>

      {/* Tooltip */}
      {showTooltip && (
        <div className="signal-widget-tooltip">
          <div className="signal-tooltip-header">
            <span className="signal-tooltip-title">{t('stats.traderMapSignal')}</span>
            <span className="signal-tooltip-label" style={{ color }}>
              {t(SIGNAL_LABEL_KEYS[label] || 'stats.neutral')}
            </span>
          </div>
          <div className="signal-tooltip-score" style={{ color }}>
            {Math.round(composite)}<span className="signal-tooltip-max">/100</span>
          </div>
          {signal.description && (
            <div className="signal-tooltip-desc">{signal.description}</div>
          )}
          <div className="signal-tooltip-bars">
            {SUB_INDICATORS.map(({ key, labelKey }) => {
              const val = signal[key] ?? 50;
              const barColor = val < 40 ? '#ef4444' : val > 60 ? '#22c55e' : '#6b7280';
              return (
                <div key={key} className="signal-tooltip-bar-row">
                  <span className="signal-tooltip-bar-lbl">{t(labelKey)}</span>
                  <div className="signal-tooltip-bar-track">
                    <div
                      className="signal-tooltip-bar-fill"
                      style={{ width: `${Math.max(2, val)}%`, background: barColor }}
                    />
                  </div>
                  <span className="signal-tooltip-bar-val">{Math.round(val)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default SignalWidget;
